import { Box, Button, Chip, IconButton, MenuItem, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { can, filterActionColumns } from '../../utils/permissions';

const TravelCountry = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');

  const token = localStorage.getItem('token');

  const [allCountries, setallCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async (signal) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/country?page=${page}&limit=${pageSize}&search=${encodeURIComponent(
          search
        )}&status=${encodeURIComponent(statusFilter)}`,
        { headers: { token }, signal }
      );
      const data = await res.json();
      if (data.success) {
        setallCountries(data.data);
        setTotalCount(data.pagination?.total || 0);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('allCountry fetch error', e);
    }
    if (!signal?.aborted) setLoading(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    getData(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, statusFilter]);

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  // Delete Country
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/country/${id}`, {
      headers: { token },
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        getData();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'CountryImage',
      headerName: 'Image',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <img
            src={cellValues.row.CountryImage}
            style={{ height: '100%', padding: '5px', aspectRatio: '16/9', objectFit: 'cover' }}
            alt="Image"
          />
        );
      },
    },
    {
      field: 'CountryName',
      headerName: 'Destination Name',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryDescription',
      headerName: 'Description',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryDuration',
      headerName: 'Duration',
      minWidth: 100,
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'CountryType',
      headerName: 'Type',
      minWidth: 100,
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'discount',
      headerName: 'Discount (EUR)',
      minWidth: 110,
      flex: 1,
      cellClassName: 'name-column--cell',
      valueGetter: (params) => {
        const val = params?.value ?? params?.row?.discount;
        const num = val == null ? 0 : typeof val === 'object' ? Number(val) : Number(val);
        const display = Number.isNaN(num) ? 0 : num;
        return `€ ${display}`;
      },
    },
    {
      field: 'CountryStatus',
      headerName: 'Status',
      minWidth: 100,
      flex: 1,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'Edit',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Link to={`/countries/${cellValues.row.CountryID}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => openDeleteDialog(cellValues.row.CountryID)}
          >
            <DeleteIcon />
          </Button>
        );
      },
    },
  ];

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="TRAVEL DESTINATION" subtitle="All Travel Destinations" />

        {can('destinations.create') && (
          <Link to={'/countries/addcountry'}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              <PublicIcon sx={{ mr: '10px' }} />
              Add Destination
            </Button>
          </Link>
        )}
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by destination name, description, type..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          sx={{ minWidth: '160px', backgroundColor: colors.primary[400] }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Box>

      {(search || statusFilter) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          {search && (
            <Chip
              label={`Search: ${search}`}
              size="small"
              onDelete={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {statusFilter && (
            <Chip
              label={`Status: ${statusFilter === 'active' ? 'Active' : 'Inactive'}`}
              size="small"
              onDelete={() => {
                setStatusFilter('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .name-column--cell': {
            color: colors.grey[100],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: colors.primary[400],
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allCountries}
          columns={filterActionColumns(columns, { Edit: 'destinations.edit', Delete: 'destinations.delete' })}
          getRowId={(row) => row.CountryID}
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete destination?"
        message="Are you sure you want to delete this destination?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TravelCountry;
